<script lang="jsx" setup>
import {
  h, Fragment, render, onMounted, onUnmounted, ref, watch, defineProps, reactive,
} from 'vue';
import { autocomplete } from '@algolia/autocomplete-js';
import EventBus from '@/event-bus';
import '@algolia/autocomplete-theme-classic';
import _ from '@/lodash';
import axios from 'axios';
import { useStore } from 'vuex';
import HintMessage from '@/components/form/partials/hint-message';
import useLocationPrettyUrl from '@/composables/use-location-pretty-url';

const store = useStore();
const searchTerm = ref();
const cancellationTokens = [];
const {
  addLocationToUrl,
  removeLocationFromUrl,
} = useLocationPrettyUrl();
let searchAutoComplete = {};

const emit = defineEmits(['update:modelValue', 'location-selected', 'location-free-text']);
const props = defineProps({
  modelValue: {
    type: String,
    default: null,
  },
  isSearchFilter: { // This prop will disable setting search params in the store and updating url strings
    type: Boolean,
    default: false,
  },
  label: {
    type: String,
    default: 'Location',
  },
  placeholder: {
    type: String,
    default: 'Search by town / postcode',
  },
  hint: {
    type: Object,
    default: () => {
    },
  },
  showRadius: {
    type: Boolean,
    default: true,
  },
  radius: {
    type: [String, Number],
    default: '0',
  },
  radiusIncrements: {
    type: Array,
    default: () => [5, 10, 15, 20, 25, 30, 35, 40, 45, 50],
  },
});

const ui = reactive({
  loading: false,
  showWarning: false,
});

if (props.isSearchFilter) {
  // Add a watcher to trigger updates when the location is read from the browser url (e.g. when the user clicks the back button)
  watch(
    () => store.state.search.query.location,
    (newValue, oldValue) => {
      if (newValue !== oldValue) {
        searchAutoComplete.setQuery(newValue);
        emit('update:modelValue', newValue);
      }
    },
    { deep: true },
  );
}

const changeRadius = (event) => {
  store.commit('search/updateQueryFilters', {
    filterValues: event.currentTarget.value,
    filterName: 'radius',
  });

  // Related to new search url format
  const parsedUrl = new URL(window.location.href);
  const radius = event.currentTarget.value;

  if (radius !== '10') {
    parsedUrl.searchParams.set('radius', radius);
  } else {
    parsedUrl.searchParams.delete('radius');
  }

  store.commit('search/updateSearchUrl', parsedUrl.pathname + parsedUrl.search);
};

const radiusLabel = (radius) => `Within ${radius} ${radius > 1 ? 'miles' : 'mile'}`;

function debouncePromise(fn, time) {
  let timerId;

  return function debounced(...args) {
    if (timerId) {
      clearTimeout(timerId);
    }

    return new Promise((resolve) => {
      timerId = setTimeout(() => resolve(fn(...args)), time);
    });
  };
}

const getResultsPromise = (query) => {
  if (query === '' || query.length < 2) return [];
  cancellationTokens.forEach((cancellationToken) => {
    cancellationToken('cancelled-request');
  });

  const config = { suggestionsUrl: '/suggestions.php' };
  const { CancelToken } = axios;
  const source = CancelToken.source();

  cancellationTokens.push(source.cancel);

  const params = {
    params: {
      s: query,
      target: 'locations',
    },
    cancelToken: source.token,
  };

  return axios.get(config.suggestionsUrl, params);
};

const debounced = debouncePromise((items) => getResultsPromise(items), 300);

const setSearchTerm = (event, query) => {
  searchTerm.value = query;
};

const submitSearch = (location) => {
  // Indicates the value is not selected from suggestions but is a free text entry
  if (!location.id) {
    location = {
      name: location,
      slug: null,
    };
    emit('location-free-text', location);
  } else {
    emit('location-selected', location);
  }

  // Unblur the field - nice to have UI
  document.activeElement.blur();

  searchAutoComplete.setIsOpen(false);
  searchAutoComplete.setQuery(location.name);
  emit('update:modelValue', location.name);
  setSearchTerm(null, location.name);

  if (props.isSearchFilter) {
    const {
      pathname,
      search,
    } = addLocationToUrl(location);

    // update search url expects the api url for the search results
    store.commit('search/updateSearchUrl', `${pathname}${search}`);

    store.commit('search/updateQueryFilters', {
      filterValues: location.name,
      filterName: 'location',
    });
  }
};

const renderSuggestion = (suggestion, searchValue) => {
  const name = _.cloneDeep(suggestion);
  const search = searchValue.replace(/[^\w\s]/gi, '');
  const regExp = new RegExp(search, 'ig');
  const matched = name.match(regExp);

  if (matched !== null) {
    return name.replace(regExp, `<strong>${matched[0]}</strong>`);
  }

  return name;
};

const ListItemHtml = (location, query) => {
  return (
      <div
          key={location.id}
          onClick={(event) => {
            event.stopPropagation();
            submitSearch(location, event);
          }}
      >
        <div className="aa-Item-meta">
          <div className="aa-Item-meta-title">
            <a
              v-html={renderSuggestion(location.name, query)}
            />
          </div>
        </div>
      </div>
  );
};

onMounted(() => {
  let query = '';

  if (props.isSearchFilter && store.state.search.query.location !== '') {
    query = (
      typeof store.state.search.locationsMeta.locations
        && store.state.search.locationsMeta.locations.length > 0
    )
      ? store.state.search.locationsMeta.locations[0].formatted_address
      : store.state.search.query.location;
  }

  EventBus.$on('bus:location-quick-filter:remove', () => {
    searchAutoComplete.setQuery('');
    searchTerm.value = '';

    emit('update:modelValue', null);

    store.commit('search/updateQueryFilters', {
      filterValues: '',
      filterName: 'location',
    });

    const { prettyUrl } = useLocationPrettyUrl({
      name: null,
      slug: null,
    });
    store.commit('search/updateSearchUrl', prettyUrl);
  });

  searchAutoComplete = autocomplete({
    container: '#autocompleteLocation',
    detachedMediaQuery: 'none',
    openOnFocus: false,
    initialState: {
      query,
    },
    placeholder: props.placeholder,
    onSubmit(suggestion) {
      submitSearch(suggestion.state.query);
    },
    onStateChange({ state }) {
      if (state.query !== props.modelValue) {
        emit('update:modelValue', state.query);
      }
    },
    onReset() {
      searchTerm.value = '';

      emit('update:modelValue', null);

      if (props.isSearchFilter) {
        store.commit('search/updateQueryFilters', {
          filterValues: '',
          filterName: 'location',
        });

        const {
          pathname,
          search,
        } = removeLocationFromUrl();

        store.commit('search/updateSearchUrl', pathname + search);
      }
    },
    getSources({ query }) { //eslint-disable-line
      return debounced(
        query,
      ).then(
        ({ data }) => {
          return [
            {
              sourceId: 'results',
              getItems() {
                if (data) {
                  const { locations } = data.data;

                  if (locations.length === 0) {
                    return [];
                  }

                  return [...locations];
                }
                return [];
              },
              getItemUrl({
                item,
              }) {
                return item.slug;
              },
              templates: {
                item({ item }) {
                  return ListItemHtml(item, query);
                },
              },
            },
          ];
        },
      )
        .catch((error) => {
          console.log(error);
        });
    },
    // Enables keyboard navigation and selection. getItemUrl passes the location slug this function
    navigator: {
      navigate({ item }) {
        submitSearch(item);
      },
    },
    renderer: {
      createElement: h,
      Fragment,
      render,
    },
  });
});

onUnmounted(() => {
  const domElement = document.querySelector('.aa-Autocomplete');
  if (domElement) {
    // in the production bundle the dom node is not removed automatically like it is in the dev bundle
    // must remove it manually
    domElement.remove();
  }
  EventBus.$off('bus:mobile-header:search', submitSearch);
  EventBus.$off('bus:location-quick-filter:remove');
  searchAutoComplete.destroy();
});
</script>

<template>
  <div class="container">
    <label class="Form-label">
      {{ props.label }}
    </label>
    <div id="autocompleteLocation" />
    <hint-message :hint="props.hint" />
    <div
      v-if="props.showRadius"
      class="Form-field"
    >
      <label class="Form-label">
        Radius:
      </label>
      <div class="Form-inputBox Has-icon far fa-chevron-down Icon-right">
        <select
          data-cy-id="search-filter-radius"
          :value="props.radius"
          class="Form-select"
          @change="changeRadius"
        >
          <option :value="0">
            Exact location
          </option>
          <option
            v-for="increments in radiusIncrements"
            :key="increments"
            :value="increments"
          >
            {{ radiusLabel(increments) }}
          </option>
        </select>
      </div>
      <transition
        name="Fade"
      >
        <div
          v-if="ui.showWarning && !searchIcon"
          class="Notice Notice--warning"
        >
          <span>We haven't seen this location before! Please check your <b>spelling</b> before you continue.</span>
        </div>
      </transition>
    </div>
  </div>
</template>

<style lang="scss" scoped>

:deep(.aa-Autocomplete) {

  .aa-InputWrapperPrefix {
    .aa-Label svg {
      fill: black
    }

    .aa-LoadingIndicator {
      //display: none;
      position: static;
      background: #ea5b1b;
      border-top-right-radius: 4px;
      border-bottom-right-radius: 4px;
    }

    .aa-ClearButton {
      display: none !important;
    }

    &:hover {
      background: #ea5b1b;

      .aa-Label svg {
        fill: #fff !important;
      }
    }
  }

  .container .Button {
    height: auto;
  }

  .aa-InputWrapperPrefix {
    order: 5;
  }

  .aa-Form {
    border-radius: 6px;
    border: 1px solid #848281;;

    &:focus-within {
      outline: 2px solid #ea5b1b !important;
      border: 1px solid #ea5b1b !important;
      // border: none !important;
      box-shadow: none !important;
    }
  }

  .aa-SubmitButton {
    //display: none;
    background: #ea5b1b;
    line-height: 1;
    border-top-right-radius: 4px;
    border-bottom-right-radius: 4px;

    .aa-SubmitIcon {
      fill: white !important;
    }

  }

  .aa-InputWrapper {
    order: 1;
    position: relative;
    width: 100%;
    padding-left: 15px;

    .aa-Input {
      padding: 0 !important;
    }
  }
}
</style>
